import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import MobileCartModal from './MobileCartModal';
import { 
  User, 
  CaretDown, 
  SignOut,
  ShoppingCart,
  Package
} from 'phosphor-react';

// Simplified cart item component for dropdown
function CartDropdownItem({ item, t }) {
  return (
    <div className="flex items-center space-x-3 p-4 border-b border-gray-50 last:border-b-0">
      <div className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
          <span className="text-xs font-medium text-ambre-600">Card</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{item.name}</h4>
        <p className="text-xs text-gray-500">{t('product.quantity')}: {item.quantity}</p>
      </div>
      <div className="text-sm font-medium text-ambre-600">
        €{(item.price * item.quantity).toFixed(2)}
      </div>
    </div>
  );
}

// Discount item component for dropdown
function CartDropdownDiscountItem({ discount, t }) {
  return (
    <div className="flex items-center space-x-3 p-4 bg-green-50 border-b border-green-100 last:border-b-0">
      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-green-600 text-sm font-bold">%</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-green-800 truncate">{discount.name}</h4>
        <p className="text-xs text-green-600">Korting toegepast</p>
      </div>
      <div className="text-sm font-medium text-green-600">
        €{discount.price.toFixed(2)}
      </div>
    </div>
  );
}

function Navbar() {
  const { t } = useTranslation();
  const { getCartItemsCount, items, getCartTotal, getCartSubtotal, getCartShipping, getDiscountItems, showCartDropdown, hideCartDropdown, showCartDropdownAction, hideCartDropdownAction, cancelAutoHideAction, showMobileCartModal, showMobileCartModalAction, hideMobileCartModalAction } = useCart();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const cartItemsCount = getCartItemsCount();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100); // Switch after 100px of scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Determine if we should use home page styling
  const shouldUseHomeStyling = isHomePage && !isScrolled;

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCartClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      showMobileCartModalAction();
    }
    // On desktop, let the Link handle navigation normally
  };

  const handleCloseMobileCart = () => {
    hideMobileCartModalAction();
  };

  // Function to determine if a link is active
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/shop') {
      // Shop should be active for /shop and /product/* pages
      return location.pathname.startsWith('/shop') || location.pathname.startsWith('/product/');
    }
    return location.pathname.startsWith(path);
  };

  // Function to get link classes
  const getLinkClasses = (path) => {
    const baseClasses = "font-medium transition pb-1 border-b-2"; // Add border-b-2 to all links to prevent layout shift
    if (isActiveLink(path)) {
      return `${baseClasses} ${isHomePage ? 'text-amber-800 border-amber-800' : 'text-ambre-600 border-ambre-600'}`;
    }
    return `${baseClasses} ${isHomePage ? 'text-amber-700 hover:text-amber-800 border-transparent' : 'text-gray-600 hover:text-ambre-500 border-transparent'}`;
  };


  return (
    <nav className={`w-full ${
      isHomePage 
        ? 'bg-transparent border-b border-amber-200' 
        : 'bg-white shadow-sm border-b border-gray-100'
    }`}>
      
      {/* Top row with logo and user actions */}
      <div className="relative py-4 px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={isHomePage ? "/assets/images/logo-orange.png" : "/assets/images/logo.png"} 
              alt="Atelier Ambre" 
              className="h-[120px] w-auto"
            />
          </Link>
          
          {/* Navigation links - desktop only - centered */}
          <div className="hidden md:flex space-x-6 absolute left-1/2 transform -translate-x-1/2">
            <Link to="/" className={getLinkClasses('/')} onClick={() => console.log('Navbar: Navigate to home')}>
              {t('nav.home')}
            </Link>
            <Link to="/shop" className={getLinkClasses('/shop')} onClick={() => console.log('Navbar: Navigate to shop')}>
              {t('nav.shop')}
            </Link>
            <Link to="/about" className={getLinkClasses('/about')} onClick={() => console.log('Navbar: Navigate to about')}>
              {t('nav.about')}
            </Link>
            <Link to="/contact" className={getLinkClasses('/contact')} onClick={() => console.log('Navbar: Navigate to contact')}>
              {t('nav.contact')}
            </Link>
          </div>
          
          <div className="flex items-center space-x-6">
            
            {/* Authentication Section */}
            {user ? (
              <div 
                className="relative"
                onMouseEnter={() => setIsUserDropdownOpen(true)}
                onMouseLeave={() => setIsUserDropdownOpen(false)}
              >
                <button className={`flex items-center space-x-2 transition ${
                  isHomePage 
                    ? 'text-amber-700 hover:text-amber-800' 
                    : 'text-gray-600 hover:text-ambre-500'
                }`}>
                  <User size={20} />
                  <span className="font-medium hidden sm:block">
                    {user.user_metadata?.first_name || user.email?.split('@')[0] || 'User'}
                  </span>
                  <CaretDown size={16} />
                </button>
                
                {/* User Dropdown */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 top-full pt-2 z-50">
                    <div className="w-56 bg-white rounded-lg shadow-lg border border-gray-200">
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{t('common.welcome')}</p>
                        <p className="text-xs text-gray-500">
                          {user.user_metadata?.first_name ? 
                            `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim() : 
                            user.email
                          }
                        </p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/my-orders"
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition flex items-center space-x-2 ${
                            isActiveLink('/my-orders') 
                              ? 'bg-ambre-50 text-ambre-700 border border-ambre-200' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <Package size={16} />
                          <span>{t('nav.orders')}</span>
                        </Link>
                        <Link
                          to="/my-profile"
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition flex items-center space-x-2 ${
                            isActiveLink('/my-profile') 
                              ? 'bg-ambre-50 text-ambre-700 border border-ambre-200' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <User size={16} />
                          <span>{t('nav.profile')}</span>
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition flex items-center space-x-2"
                        >
                          <SignOut size={16} />
                          <span>{t('nav.logout')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className={`flex items-center space-x-2 transition ${
                isHomePage 
                  ? (isActiveLink('/login') ? 'text-amber-800' : 'text-amber-700 hover:text-amber-800')
                  : (isActiveLink('/login') ? 'text-ambre-600' : 'text-gray-600 hover:text-ambre-500')
              }`}>
                <User size={20} />
                <span className="font-medium hidden sm:block">{t('nav.login')}</span>
              </Link>
            )}
            
            {/* Cart with Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => {
                // Only show dropdown on desktop
                if (!isMobile) {
                  showCartDropdownAction();
                  cancelAutoHideAction();
                }
              }}
              onMouseLeave={(e) => {
                // Only hide if we're not moving to the dropdown and on desktop
                if (!isMobile) {
                  const relatedTarget = e.relatedTarget;
                  if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
                    hideCartDropdownAction();
                  }
                }
              }}
            >
              <Link 
                to="/cart" 
                onClick={handleCartClick}
                className={`flex items-center space-x-2 transition relative ${
                  isHomePage 
                    ? (isActiveLink('/cart') ? 'text-amber-800' : 'text-amber-700 hover:text-amber-800')
                    : (isActiveLink('/cart') ? 'text-ambre-600' : 'text-gray-600 hover:text-ambre-500')
                }`}
              >
                <ShoppingCart size={20} />
                <span className="font-medium hidden sm:block">{t('nav.cart')}</span>
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-ambre-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemsCount}
                  </span>
                )}
              </Link>
              
              {/* Cart Dropdown - Hide on mobile when mobile cart modal is open */}
              {showCartDropdown && !isMobile && (
                <div 
                  className="absolute right-0 top-full pt-2 z-50"
                  onMouseEnter={() => {
                    if (!isMobile) {
                      showCartDropdownAction();
                      cancelAutoHideAction();
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isMobile) {
                      hideCartDropdownAction();
                    }
                  }}
                >
                  <div className="w-96 bg-white rounded-lg shadow-lg border border-gray-200">
                    {items.length === 0 ? (
                      <div className="p-6 text-center">
                        <ShoppingCart size={48} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">{t('cart.empty')}</p>
                        <Link 
                          to="/shop" 
                          className="inline-block bg-ambre-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-ambre-700 transition"
                          onClick={() => hideCartDropdownAction()}
                        >
                          {t('shop.viewDetails')}
                        </Link>
                      </div>
                    ) : (
                      <>
                        <div className="p-4 border-b border-gray-100">
                          <h3 className="font-semibold text-gray-900">{t('cart.title')} ({cartItemsCount} {cartItemsCount === 1 ? t('common.item') : t('common.items')})</h3>
                        </div>
                        
                        <div className="max-h-64 overflow-y-auto">
                          {items.map((item) => (
                            <CartDropdownItem key={item.id} item={item} t={t} />
                          ))}
                          
                          {getDiscountItems().map((discount) => (
                            <CartDropdownDiscountItem key={discount.id} discount={discount} t={t} />
                          ))}
                        </div>
                        
                        <div className="p-4 border-t border-gray-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">{t('cart.shipping')}:</span>
                            <span className="text-sm text-gray-600">
                              {getCartShipping() === 0 ? 'Gratis' : `€${getCartShipping().toFixed(2)}`}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-4">
                            <span className="font-medium text-gray-900">{t('cart.total')}:</span>
                            <span className="font-bold text-lg text-ambre-600">€{getCartTotal().toFixed(2)}</span>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => {
                                hideCartDropdownAction();
                                navigate('/cart');
                              }}
                              className="w-full bg-ambre-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-ambre-700 transition text-sm"
                            >
                              {t('cart.checkout')}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile navigation links - below the main row */}
      <div className={`md:hidden px-8 pb-4 border-t relative z-10 ${
        shouldUseHomeStyling ? 'border-white/20' : 'border-gray-100'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center space-x-6 pt-3">
            <Link to="/" className={getLinkClasses('/')} onClick={() => console.log('Mobile Navbar: Navigate to home')}>
              {t('nav.home')}
            </Link>
            <Link to="/shop" className={getLinkClasses('/shop')} onClick={() => console.log('Mobile Navbar: Navigate to shop')}>
              {t('nav.shop')}
            </Link>
            <Link to="/about" className={getLinkClasses('/about')} onClick={() => console.log('Mobile Navbar: Navigate to about')}>
              {t('nav.about')}
            </Link>
            <Link to="/contact" className={getLinkClasses('/contact')} onClick={() => console.log('Mobile Navbar: Navigate to contact')}>
              {t('nav.contact')}
            </Link>
          </div>
        </div>
      </div>
      
      {/* Mobile Cart Modal */}
      <MobileCartModal 
        isOpen={showMobileCartModal} 
        onClose={handleCloseMobileCart} 
      />
    </nav>
  );
}

export default Navbar; 